import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Provider } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

const columns: readonly Column<Provider>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (p) => (
      <strong>
        {p.lastName}, {p.firstName}
        {p.suffix ? ` ${p.suffix}` : ''}
      </strong>
    ),
  },
  { key: 'npi', header: 'NPI', render: (p) => p.npi },
  { key: 'specialty', header: 'Specialty', render: (p) => p.specialty || '--' },
  { key: 'email', header: 'Email', render: (p) => p.email || '--' },
  {
    key: 'isSystem',
    header: 'System',
    render: (p) => (p.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
  },
  { key: 'status', header: 'Status', render: (p) => <StatusBadge status={p.isActive ? 'active' : 'inactive'} /> },
]

export function ProviderListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage } = useApiList<Provider>({
    path: '/providers',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('providers')

  return (
    <div>
      <PageHeader
        title="Providers"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/providers/new')} style={btnPrimary}>
              Add Provider
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by name or NPI..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(p) => navigate(`/providers/${p.id}`)}
            emptyMessage="No providers found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
